import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { programsAPI } from "../services/api";
import { categoryLabels } from "../utils/helpers";
import { PageWrapper, Card, Button, Logo } from "../components";
import { 
  Clock, Layers, Sparkles, Check, ChevronRight, 
  ArrowLeft, Play, Compass, Map, Heart
} from "lucide-react";

// Suggested journey order (inspirational, not mandatory)
const SUGGESTED_PATH = [
  { category: "alignment", stage: 1, description: "Begin with grounding" },
  { category: "abundance", stage: 2, description: "Open to receiving" },
  { category: "true_essence", stage: 3, description: "Discover your truth" },
  { category: "liberation", stage: 4, description: "Release what no longer serves" },
  { category: "peace", stage: 5, description: "Find inner stillness" },
  { category: "radiance", stage: 6, description: "Shine your light" },
  { category: "strength", stage: 7, description: "Embody your power" },
  { category: "well_being", stage: 8, description: "Integrate wholeness" },
];

export const ProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("explore"); // "explore" or "journey"
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await programsAPI.getAll();
        setPrograms(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  if (loading) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse-gentle text-[#4A6741]">Loading...</div>
        </div>
      </PageWrapper>
    );
  }

  const activePrograms = programs.filter(p => p.status !== "coming_soon");
  const comingSoonPrograms = programs.filter(p => p.status === "coming_soon");

  // Sort programs by suggested path order (for journey view)
  const sortedByJourney = [...activePrograms].sort((a, b) => {
    const aIdx = SUGGESTED_PATH.findIndex(p => p.category === a.category);
    const bIdx = SUGGESTED_PATH.findIndex(p => p.category === b.category);
    return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
  });

  return (
    <PageWrapper>
      <div className="p-6 space-y-6 max-w-lg mx-auto">
        {/* Header with Logo */}
        <div className="flex items-center justify-center">
          <Logo size="md" variant="dark" />
        </div>

        <div className="space-y-1">
          <h1 className="font-heading text-3xl text-[#292524]">Programs</h1>
          <p className="text-[#78716C]">Your transformation, your way</p>
        </div>

        {/* View Toggle */}
        <div className="flex gap-1.5 p-1.5 bg-[#F0EFEB] rounded-full">
          <motion.button
            onClick={() => setViewMode("explore")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-200 ${
              viewMode === "explore" 
                ? "bg-white text-[#2D4A2A] shadow-md" 
                : "text-[#78716C] hover:text-[#4A4F4A]"
            }`}
            data-testid="explore-view-btn"
          >
            <Compass size={16} />
            Explore Freely
          </motion.button>
          <motion.button
            onClick={() => setViewMode("journey")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-200 ${
              viewMode === "journey" 
                ? "bg-white text-[#2D4A2A] shadow-md" 
                : "text-[#78716C] hover:text-[#4A4F4A]"
            }`}
            data-testid="journey-view-btn"
          >
            <Map size={16} />
            Suggested Path
          </motion.button>
        </div>

        {/* Explore View - Freedom to choose */}
        {viewMode === "explore" && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Available Programs */}
            <div className="space-y-4">
              <h2 className="font-heading text-lg text-[#292524] flex items-center gap-2">
                <Heart size={18} className="text-[#D4A373]" />
                Follow Your Heart
              </h2>
              <p className="text-sm text-[#78716C] -mt-2">
                Every program is open to you. Trust your intuition.
              </p>
              {activePrograms.map((program, idx) => {
                // Check if this is a Journey program
                const isJourneyProgram = program.category === "abundance";
                
                return (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  whileHover={{ y: -4, boxShadow: "0 16px 40px rgba(26,29,26,0.1)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card
                    className="overflow-hidden group"
                    interactive
                    onClick={() => navigate(isJourneyProgram ? "/journey/abundance" : `/programs/${program.id}`)}
                    data-testid={`program-${program.id}`}
                  >
                    <div className="relative overflow-hidden">
                      <motion.div 
                        className="h-44 bg-cover bg-center"
                        style={{ backgroundImage: `url(${program.cover_image})` }}
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.4 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      {program.category && (
                        <span className="absolute top-3 left-3 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-[#4A6741] shadow-sm">
                          {categoryLabels[program.category] || program.category}
                        </span>
                      )}
                      {isJourneyProgram && (
                        <span className="absolute top-3 right-3 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-xs font-semibold shadow-sm">
                          21-Day Journey
                        </span>
                      )}
                    </div>
                    <div className="p-5 space-y-3">
                      <h3 className="font-heading text-xl text-[#292524] group-hover:text-[#4A6741] transition-colors duration-200">{program.title}</h3>
                      <p className="text-[#78716C] line-clamp-2 leading-relaxed">{program.description}</p>
                      <div className="flex items-center gap-4 text-sm text-[#78716C]">
                        {isJourneyProgram ? (
                          <span className="flex items-center gap-1.5 text-emerald-600">
                            <Sparkles size={15} /> 21 Days • Interactive
                          </span>
                        ) : (
                          <>
                            <span className="flex items-center gap-1.5">
                              <Layers size={15} className="text-[#9CA3AF]" /> {program.modules?.length || 0} modules
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Clock size={15} className="text-[#9CA3AF]" /> {program.estimated_hours}h
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );})}
            </div>

            {/* Coming Soon */}
            {comingSoonPrograms.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-lg text-[#292524] flex items-center gap-2">
                  <Clock size={18} className="text-[#D4A373]" />
                  Coming Soon
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {comingSoonPrograms.map((program, idx) => (
                    <Card
                      key={program.id}
                      className="opacity-90 overflow-hidden"
                      interactive
                      onClick={() => navigate(`/programs/${program.id}`)}
                      data-testid={`program-coming-${program.id}`}
                    >
                      <div className="relative">
                        <div 
                          className="h-24 bg-cover bg-center"
                          style={{ backgroundImage: `url(${program.cover_image})` }}
                        />
                        <div className="absolute inset-0 bg-black/20" />
                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-[#D4A373] text-white text-xs rounded-full">
                          Soon
                        </span>
                      </div>
                      <div className="p-3 space-y-1">
                        <h3 className="font-heading text-sm text-[#292524] line-clamp-2">{program.title}</h3>
                        <p className="text-xs text-[#78716C]">{program.estimated_hours}h</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Journey View - Suggested Path */}
        {viewMode === "journey" && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Journey Introduction */}
            <Card className="p-5 bg-gradient-to-br from-[#2D4A2A]/5 to-[#D4A373]/5 border border-[#E8E6E2]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#2D4A2A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Map size={24} className="text-[#2D4A2A]" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-heading text-lg text-[#1F2937]">The Reclaim Path</h3>
                  <p className="text-sm text-[#4A4F4A] leading-relaxed">
                    A suggested journey through consciousness. This path is an invitation, not a requirement. 
                    <span className="font-medium text-[#2D4A2A]"> Feel free to start anywhere that calls to you.</span>
                  </p>
                </div>
              </div>
            </Card>

            {/* Journey Steps */}
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-[#2D4A2A]/30 via-[#D4A373]/30 to-[#2D4A2A]/30" />
              
              <div className="space-y-4">
                {sortedByJourney.map((program, idx) => {
                  const pathInfo = SUGGESTED_PATH.find(p => p.category === program.category);
                  const stage = pathInfo?.stage || idx + 1;
                  
                  return (
                    <motion.div
                      key={program.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="relative"
                    >
                      {/* Stage Marker */}
                      <div className="absolute left-0 top-4 w-12 h-12 bg-white rounded-full border-2 border-[#E8E6E2] flex items-center justify-center z-10 shadow-sm">
                        <span className="font-heading text-lg text-[#2D4A2A]">{stage}</span>
                      </div>
                      
                      {/* Program Card */}
                      <Card
                        className="ml-16 overflow-hidden"
                        interactive
                        onClick={() => navigate(`/programs/${program.id}`)}
                        data-testid={`journey-program-${program.id}`}
                      >
                        <div className="flex">
                          <div 
                            className="w-24 h-full min-h-[100px] bg-cover bg-center flex-shrink-0"
                            style={{ backgroundImage: `url(${program.cover_image})` }}
                          />
                          <div className="p-4 flex-1 space-y-2">
                            {pathInfo && (
                              <p className="text-xs text-[#D4A373] font-medium uppercase tracking-wider">
                                {pathInfo.description}
                              </p>
                            )}
                            <h3 className="font-heading text-lg text-[#292524] line-clamp-1">{program.title}</h3>
                            <p className="text-sm text-[#78716C] line-clamp-2">{program.description}</p>
                            <div className="flex items-center gap-3 text-xs text-[#9A9F9A]">
                              <span className="flex items-center gap-1">
                                <Layers size={12} /> {program.modules?.length || 0} modules
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={12} /> {program.estimated_hours}h
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Encouragement */}
            <Card className="p-5 text-center bg-[#FAF9F7] border border-[#E8E6E2]">
              <p className="text-sm text-[#4A4F4A] italic">
                "There is no wrong way to journey inward. Trust yourself."
              </p>
            </Card>

            {/* Coming Soon in Journey View */}
            {comingSoonPrograms.length > 0 && (
              <div className="space-y-4 pt-4">
                <h2 className="font-heading text-lg text-[#292524] flex items-center gap-2">
                  <Sparkles size={18} className="text-[#D4A373]" />
                  More Adventures Coming
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {comingSoonPrograms.map((program) => (
                    <Card
                      key={program.id}
                      className="opacity-80 overflow-hidden"
                      interactive
                      onClick={() => navigate(`/programs/${program.id}`)}
                    >
                      <div className="relative">
                        <div 
                          className="h-20 bg-cover bg-center"
                          style={{ backgroundImage: `url(${program.cover_image})` }}
                        />
                        <div className="absolute inset-0 bg-black/20" />
                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-[#D4A373] text-white text-xs rounded-full">
                          Soon
                        </span>
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading text-sm text-[#292524] line-clamp-1">{program.title}</h3>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </PageWrapper>
  );
};

export const ProgramDetailPage = () => {
  const { programId } = useParams();
  const [program, setProgram] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Programs that use the new Journey Experience System
  const JOURNEY_PROGRAMS = {
    "abundance": "/journey/abundance"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programRes, progressRes] = await Promise.all([
          programsAPI.getById(programId),
          programsAPI.getProgress(programId)
        ]);
        setProgram(programRes.data);
        setProgress(progressRes.data);
        
        // Check if this program uses the new Journey system
        const journeyPath = JOURNEY_PROGRAMS[programRes.data.category];
        if (journeyPath) {
          // Redirect to the new Journey experience
          navigate(journeyPath, { replace: true });
          return;
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [programId, navigate]);

  const handleContinue = () => {
    if (program?.status === "coming_soon") return;
    if (progress?.next_lesson_id) {
      const lesson = program.modules
        .flatMap(m => m.lessons)
        .find(l => l.id === progress.next_lesson_id);
      if (lesson) {
        navigate(`/lesson/${lesson.id}?program=${programId}&module=${lesson.module_id}`);
      }
    } else if (program?.modules?.[0]?.lessons?.[0]) {
      const firstLesson = program.modules[0].lessons[0];
      navigate(`/lesson/${firstLesson.id}?program=${programId}&module=${program.modules[0].id}`);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse-gentle text-[#4A6741]">Loading...</div>
        </div>
      </PageWrapper>
    );
  }

  if (!program) return null;

  const template = program.template;
  const isComingSoon = program.status === "coming_soon";

  return (
    <PageWrapper>
      <div className="relative">
        {/* Hero */}
        <div 
          className="h-72 bg-cover bg-center relative"
          style={{ backgroundImage: `url(${program.cover_image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F7] via-[#FAF9F7]/60 to-transparent" />
          <button
            onClick={() => navigate("/programs")}
            className="absolute top-4 left-4 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center shadow-sm"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-[#1A1D1A]" />
          </button>
          {program.category && (
            <span className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur rounded-full text-xs font-semibold text-[#2D4A2A]">
              {categoryLabels[program.category] || program.category}
            </span>
          )}
        </div>

        {/* Content - with negative margin to overlap hero slightly */}
        <div className="relative px-6 -mt-8 space-y-6">
          {/* Coming Soon Badge - positioned above title */}
          {isComingSoon && (
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#B8956C] text-white rounded-full text-sm font-semibold shadow-md">
              <Clock size={16} /> Coming Soon
            </span>
          )}
          
          {/* Title & Description */}
          <div className="space-y-3">
            <h1 className="font-heading text-3xl text-[#1A1D1A]">{program.title}</h1>
            <p className="text-[#4A4F4A] leading-relaxed">{program.description}</p>
            
            <div className="flex items-center gap-4 text-sm text-[#7A7F7A]">
              <span className="flex items-center gap-1.5">
                <Layers size={16} /> {program.modules?.length || 0} modules
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={16} /> {program.estimated_hours}h total
              </span>
            </div>
          </div>

          {/* Progress (if active) */}
          {!isComingSoon && progress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#7A7F7A]">Your Progress</span>
                <span className="text-[#2D4A2A] font-semibold">{progress.percentage}%</span>
              </div>
              <div className="h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#2D4A2A] rounded-full transition-all duration-500"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Start/Continue Button */}
          {!isComingSoon ? (
            <Button onClick={handleContinue} className="w-full" data-testid="continue-program-btn">
              {progress?.completed_lessons > 0 ? "Continue Program" : "Start Program"}
            </Button>
          ) : (
            <Button variant="soft" disabled className="w-full opacity-80">
              <Clock size={18} /> Notify Me When Available
            </Button>
          )}

          {/* Template Sections */}
          {template && (
            <div className="space-y-6 pt-6 border-t border-[#E8E6E1]">
              {template.overview && (
                <section className="space-y-2">
                  <h2 className="font-heading text-xl text-[#1A1D1A]">Overview</h2>
                  <p className="text-[#4A4F4A] leading-relaxed">{template.overview}</p>
                </section>
              )}

              {template.what_youll_experience?.length > 0 && (
                <section className="space-y-3">
                  <h2 className="font-heading text-xl text-[#1A1D1A]">What You'll Experience</h2>
                  <ul className="space-y-2.5">
                    {template.what_youll_experience.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="text-[#2D4A2A] flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-[#4A4F4A]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {template.daily_structure && (
                <section className="space-y-2">
                  <h2 className="font-heading text-xl text-[#1A1D1A]">Daily Structure</h2>
                  <Card className="p-5 bg-[#F7F6F3]">
                    <p className="text-[#4A4F4A] leading-relaxed">{template.daily_structure}</p>
                  </Card>
                </section>
              )}

              {template.outcomes?.length > 0 && (
                <section className="space-y-3">
                  <h2 className="font-heading text-xl text-[#1A1D1A]">Outcomes</h2>
                  <ul className="space-y-2.5">
                    {template.outcomes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Sparkles className="text-[#B8956C] flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-[#4A4F4A]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}

          {/* Modules (only for active programs with content) */}
          {!isComingSoon && program.modules?.length > 0 && (
            <div className="space-y-4 pt-6 border-t border-[#E8E6E1]">
              <h2 className="font-heading text-xl text-[#1A1D1A]">Program Content</h2>
              {program.modules.map((module, modIdx) => (
                <Card key={module.id} className="p-5 space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#7A7F7A] font-semibold">Module {modIdx + 1}</p>
                    <h3 className="font-heading text-lg text-[#1A1D1A] mt-1">{module.title}</h3>
                    <p className="text-sm text-[#4A4F4A] mt-1">{module.description}</p>
                  </div>
                  <div className="space-y-2">
                    {module.lessons?.map((lesson) => (
                      <button
                        key={lesson.id}
                        onClick={() => navigate(`/lesson/${lesson.id}?program=${programId}&module=${module.id}`)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F7F6F3] transition-colors text-left"
                        data-testid={`lesson-${lesson.id}`}
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-[#E8E6E1] text-[#7A7F7A]">
                          <Play size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1A1D1A] truncate">{lesson.title}</p>
                          <p className="text-sm text-[#7A7F7A]">{lesson.estimated_minutes} min</p>
                        </div>
                        <ChevronRight className="text-[#9A9F9A] flex-shrink-0" size={20} />
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Coming Soon - No Content Yet */}
          {isComingSoon && (!program.modules || program.modules.length === 0) && (
            <Card className="p-6 text-center space-y-3 bg-[#F7F6F3]">
              <Clock className="mx-auto text-[#B8956C]" size={32} />
              <p className="text-[#4A4F4A]">
                This program is currently in development. Content will be available soon.
              </p>
            </Card>
          )}

          {/* Bottom CTA */}
          {!isComingSoon && (
            <Button onClick={handleContinue} className="w-full" data-testid="bottom-continue-btn">
              {progress?.completed_lessons > 0 ? "Continue Program" : "Start Program"}
            </Button>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};
